import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from '@/i18n/routing';
import { useMediaQuery } from 'react-responsive';
import { Input } from '@/component/Form/Input';
import { SelectBox } from '@/component/Form/Select';
import { Button } from '@/component/Form/Button';
import { HomeAnimationIcon } from '@/component/Animation/Home/Icon';
import { usePopupWizard } from '@/component/Modal';
import { useIsClientSide } from '@/utils/next';
import {
  useFormField,
  useBedroom,
  useAddressAutocomplete,
  useFocusFirstError,
} from './form';
import { useCreateLead } from './lead';
import { HomeOwnerHeader } from './HomeOwnerHeader';
import { ModalForm } from './ModalForm';
import { Address } from './Address';
import { ModalFormAdress } from './ModalFormAdress';
import { ModalFormBedrooms } from './ModalFormBedrooms';

export const HomeOwnerDetailed = ({ title, desc, findMore, defaultValues }) => {
  const t = useTranslations();
  const router = useRouter();
  const { onFocusError, containerRef: errorContainerRef } =
    useFocusFirstError();
  const { onCreate, loading: leadLoading, validationError } = useCreateLead();

  const isClient = useIsClientSide();
  const isMobileDevice = useMediaQuery({ query: '(max-width: 640px)' });
  const isMobile = Boolean(isClient && isMobileDevice);
  const {
    currentStep,
    onSetStep,
    onReset: onCloseModal,
  } = usePopupWizard(['address', 'name', 'bedrooms', 'email', 'phone']);

  const {
    bedroom,
    options,
    onSelectBedroom,
    onValidateBedroom,
    onBlur: onBedroomBlur,
    isBedroomInvalid,
  } = useBedroom(defaultValues?.home?.numBedrooms);

  const {
    onBlur: onAddressBlur,
    onSelectAddress,
    onResetAddress,
    onAddressChange,
    opened,
    containerRef,
    addresses,
    loading,
    validationError: addressError,
    isAddressInvalid,
    onOpenDropdown,
    address,
    onValidateAddress,
  } = useAddressAutocomplete(defaultValues?.autocompletedAddress);

  const {
    value: name,
    onChange: handleNameChange,
    onReset: handleResetName,
    error: nameError,
    invalid: nameInvalid,
    onBlur: onNameBlur,
  } = useFormField();

  const handlePhoneValidateCustom = useCallback((phoneValue) => {
    if (!validatePhone(phoneValue)) {
      return 'form.input.phone.error';
    }
    return '';
  }, []);
  const {
    value: phone,
    onChange: handlePhone,
    onReset: handleResetPhone,
    onValidate: handlePhoneValidate,
    error: phoneError,
    invalid: phoneInvalid,
    onBlur: onPhoneBlur,
  } = useFormField({
    onValidate: handlePhoneValidateCustom,
  });

  const handleEmailValidateCustom = useCallback((emailValue) => {
    if (!validateEmail(emailValue)) {
      return 'form.input.email.error';
    }
    return '';
  }, []);
  const {
    value: email,
    onChange: handleEmail,
    onReset: handleResetEmail,
    onValidate: handleEmailValidate,
    error: emailError,
    invalid: emailInvalid,
    onBlur: onEmailBlur,
  } = useFormField({
    onValidate: handleEmailValidateCustom,
  });

  const onCreateLead = () => {
    const addressItem = addresses.find((addr) => addr === address);
    onCreate({
      name,
      email,
      phone,
      address,
      numBedrooms: bedroom.value,
      city: addressItem.city,
      zipCode: addressItem.zipCode,
      pageName: 'owner landing',
    }).then((success) => {
      if (!success) return;
      router.push({
        pathname: '/onboarding/rental-estimation/success',
      });
    });
  };

  const onWantToRent = () => {
    const addrError = onValidateAddress();
    const roomError = onValidateBedroom();
    const mobileError = handlePhoneValidate();
    const mailError = handleEmailValidate();
    if (addrError || roomError || mailError || mobileError) {
      requestAnimationFrame(onFocusError);
      return;
    }
    onCreateLead();
  };

  const onWantToRentMobile = () => {
    const addrError = onValidateAddress();
    const roomError = onValidateBedroom();
    const mobileError = handlePhoneValidate();
    const mailError = handleEmailValidate();
    if (addrError) {
      onSetStep('address');
      return;
    }
    if (roomError) {
      onSetStep('bedrooms');
      return;
    }
    if (mailError) {
      onSetStep('email');
      return;
    }
    if (mobileError) {
      onSetStep('phone');
      return;
    }
    onCreateLead();
  };

  return (
    <div
      className="container !bg-primary-800 pt-8 pb-[46px] sm:max-lg:pt-[46px] sm:max-lg:pb-[56px] lg:pt-16 lg:pb-[72px] 3xl:rounded-10"
      ref={errorContainerRef}
    >
      <div className="flex flex-col gap-[22px] lg:gap-8">
        <HomeOwnerHeader title={title} desc={desc} />
        <div className="flex max-lg:flex-wrap gap-y-3 gap-x-10 w-full justify-between">
          <div className="flex flex-col justify-between w-full">
            <div className="grid max-md:grid-cols-1 lg:grid-cols-2 w-auto lg:max-w-[844px] max-lg:flex-col max-lg:gap-8 lg:gap-6">
              <Address
                containerRef={containerRef}
                isMobile={isMobile}
                isAddressInvalid={isAddressInvalid}
                address={address}
                addressError={addressError}
                onAddressChange={onAddressChange}
                onAddressBlur={onAddressBlur}
                onSetStep={onSetStep}
                onOpenDropdown={onOpenDropdown}
                addresses={addresses}
                disabled={leadLoading}
                onSelectAddress={onSelectAddress}
                loading={loading}
                onResetAddress={onResetAddress}
                opened={opened}
                className="lg:col-span-2"
              />
              <div className="w-full lg:max-w-[410px]">
                <Input
                  icon="icon-user"
                  label="form.input.name.label"
                  placeholder="form.input.name.placeholder"
                  withoutBG
                  error={isMobile && nameInvalid}
                  errorText={!isMobile && nameError}
                  onChange={handleNameChange}
                  value={name}
                  onBlur={onNameBlur}
                  onClick={isMobile ? () => onSetStep('name') : undefined}
                  onReset={(e) => {
                    if (isMobile) {
                      e.stopPropagation();
                    }
                    handleResetName(e);
                  }}
                  disabled={leadLoading}
                  readOnly={isMobile}
                  theme="outlined"
                  size="md"
                />
              </div>
              <div className="w-full lg:max-w-[410px]">
                <SelectBox
                  options={options}
                  icon="icon-bedroom"
                  label="form.input.bedrooms.label"
                  placeholder="form.input.bedrooms.placeholder"
                  withoutBG
                  error={!isMobile && isBedroomInvalid}
                  onChange={onSelectBedroom}
                  value={bedroom}
                  onBlur={onBedroomBlur}
                  dataTestid="inquiryBedrooms"
                  disabled={leadLoading}
                  onClick={isMobile ? () => onSetStep('bedrooms') : undefined}
                  menuIsOpen={isMobile ? false : undefined}
                  onMenuOpen={
                    isMobile ? () => onSetStep('bedrooms') : undefined
                  }
                />
              </div>
              <div className="w-full lg:max-w-[410px]">
                <Input
                  icon="icon-message"
                  label="form.input.email.label"
                  placeholder="form.input.email.placeholder"
                  withoutBG
                  error={!isMobile && emailInvalid}
                  errorText={!isMobile && emailError}
                  onChange={handleEmail}
                  value={email}
                  onClick={isMobile ? () => onSetStep('email') : undefined}
                  onBlur={onEmailBlur}
                  onReset={(e) => {
                    if (isMobile) {
                      e.stopPropagation();
                    }
                    handleResetEmail();
                  }}
                  dataTestid="inquiryEmail"
                  disabled={leadLoading}
                  readOnly={isMobile}
                  theme="outlined"
                  size="md"
                />
              </div>
              <div className="w-full lg:max-w-[410px]">
                <Input
                  icon="icon-phone"
                  label="form.input.phone.label"
                  placeholder="form.input.phone.placeholder"
                  withoutBG
                  error={!isMobile && phoneInvalid}
                  errorText={!isMobile && phoneError}
                  onChange={handlePhone}
                  value={phone}
                  onClick={isMobile ? () => onSetStep('phone') : undefined}
                  onBlur={onPhoneBlur}
                  onReset={(e) => {
                    if (isMobile) {
                      e.stopPropagation();
                    }
                    handleResetPhone();
                  }}
                  dataTestid="inquiryPhone"
                  disabled={leadLoading}
                  readOnly={isMobile}
                  theme="outlined"
                  size="md"
                  mask
                />
              </div>
            </div>
            <div className="relative">
              <Button
                className="mt-11 self-start"
                onClick={isMobile ? onWantToRentMobile : onWantToRent}
                icon="icon-rocket"
                loading={leadLoading}
                dataTestId="inquirySubmit"
                theme="amber"
                size="lg"
              >
                {t('form.home-owner.button')}
              </Button>
              {validationError && (
                <div className="absolute -bottom-6 text-white text-xs font-normal leading-[18px] truncate max-sm:inset-x-0">
                  {validationError}
                </div>
              )}
            </div>
          </div>
          <div className="max-lg:w-full flex justify-end items-end">
            <HomeAnimationIcon />
          </div>
        </div>
      </div>

      {currentStep === 'address' && (
        <ModalFormAdress
          onCloseModal={onCloseModal}
          onSetStep={onSetStep}
          loading={leadLoading}
          isAddressInvalid={isAddressInvalid}
          onAddressChange={onAddressChange}
          address={address}
          addresses={addresses}
          onOpenDropdown={onOpenDropdown}
          onAddressBlur={onAddressBlur}
          onResetAddress={onResetAddress}
          onSelectAddress={onSelectAddress}
          nextStep="name"
          nextButtonText="form.home-owner.modal-full-button-address"
        />
      )}
      {currentStep === 'name' && (
        <ModalForm
          onClose={onCloseModal}
          buttons={
            <>
              <Button theme="text" size="md" onClick={onCloseModal}>
                {'form.home-owner.modal-button-close'}
              </Button>
              <Button
                onClick={() => onSetStep('bedrooms')}
                loading={leadLoading}
                size="md"
              >
                {t('form.home-owner.modal-full-button-name')}
              </Button>
            </>
          }
        >
          <Input
            icon="icon-user"
            label="form.input.name.label"
            placeholder="form.input.name.placeholder"
            error={nameInvalid}
            errorText={nameError}
            onChange={handleNameChange}
            value={name}
            onBlur={onNameBlur}
            onReset={(e) => {
              if (isMobile) {
                e.stopPropagation();
              }
              handleResetName(e);
            }}
            disabled={leadLoading}
            theme="filled"
            size="lg"
            autoFocus
          />
        </ModalForm>
      )}
      {currentStep === 'bedrooms' && (
        <ModalFormBedrooms
          onCloseModal={onCloseModal}
          handleSubmitMobile={() => onSetStep('email')}
          loading={leadLoading}
          isBedroomInvalid={isBedroomInvalid}
          bedroom={bedroom}
          onBedroomBlur={onBedroomBlur}
          options={options}
          onSelectBedroom={onSelectBedroom}
          nextButtonText="form.home-owner.modal-full-button-bedrooms"
        />
      )}
      {currentStep === 'email' && (
        <ModalForm
          onClose={onCloseModal}
          buttons={
            <>
              <Button theme="text" size="md" onClick={onCloseModal}>
                {'form.home-owner.modal-button-close'}
              </Button>
              <Button
                onClick={() => onSetStep('phone')}
                loading={leadLoading}
                size="md"
              >
                {t('form.home-owner.modal-full-button-email')}
              </Button>
            </>
          }
        >
          <Input
            icon="icon-message"
            label="form.input.email.label"
            placeholder="form.input.email.placeholder"
            error={emailInvalid}
            errorText={emailError}
            onChange={handleEmail}
            value={email}
            onBlur={onEmailBlur}
            onReset={(e) => {
              if (isMobile) {
                e.stopPropagation();
              }
              handleResetEmail();
            }}
            disabled={leadLoading}
            theme="filled"
            size="lg"
            autoFocus
          />
        </ModalForm>
      )}
      {currentStep === 'phone' && (
        <ModalForm
          onClose={onCloseModal}
          buttons={
            <div className="relative flex justify-between w-full">
              <Button theme="text" size="md" onClick={onCloseModal}>
                {'form.home-owner.modal-button-close'}
              </Button>
              <Button
                onClick={onWantToRentMobile}
                icon="icon-rocket"
                loading={leadLoading}
                size="md"
              >
                {t('form.home-owner.button')}
              </Button>
              {validationError && (
                <div
                  className={`
                absolute -bottom-6 md:w-full
                text-right text-slate-600 text-xs font-normal leading-[18px] truncate max-sm:inset-x-0
                `}
                >
                  {validationError}
                </div>
              )}
            </div>
          }
        >
          <Input
            icon="icon-phone"
            label="form.input.phone.label"
            placeholder="form.input.phone.placeholder"
            error={phoneInvalid}
            errorText={phoneError}
            onChange={handlePhone}
            value={phone}
            onBlur={onPhoneBlur}
            onReset={(e) => {
              if (isMobile) {
                e.stopPropagation();
              }
              handleResetPhone();
            }}
            disabled={leadLoading}
            theme="filled"
            size="lg"
            autoFocus
            mask
          />
        </ModalForm>
      )}
    </div>
  );
};

function validatePhone(phone = '') {
  return /^\d{10,15}$/.test(phone.replace('+', '').replaceAll(' ', ''));
}

function validateEmail(email = '') {
  const [user, domain] = email.split('@');
  return user && domain;
}
